import { IPersonalizationAuthConfig, IUserFact, IUserFactManifest, IUserFactMap, IUserFactRulesMap } from "../Common/Models";
import * as Configuration from "../Configuration/Configuration";
import { getSamplingRate, getSetting } from "../Common/Settings";
import { ContentType } from "../Common/ContentType";
import { EventIds } from "../Logging/Constants";
import * as Logger from "../Logging/Logger";
import { updateCache } from "../Utils/Caching";
import { AppLastUsedEventNames } from "./Constants";
import { EvaluateEvent, EvaluateTelemetryEvent } from "./UserFactManifest";
import { addOrUpdateAppUsageUserFact, createStringOfFactNames, fetchUserFactsWithTimeout, getUserFactNames } from "./UserFactsClient";
import { getCachedUserFacts, getUnExpiredCachedUserFacts, UserFactLocalStorageKey } from "./CachedUserFacts";
import { TelemetryEvent } from "@microsoft/oteljs";
import { getErrorMessage } from "../Utils/Utils";

const authTokenConfigProd: IPersonalizationAuthConfig = {
	scenario: "tulips",
	appId: "https://attributes.engagement.office.com/UserFacts.Read",
};

export function mapUserFacts(userFacts: IUserFact[]): IUserFactMap {
	if (!userFacts) {
		return null;
	}

	const userFactMap: IUserFactMap = {};
	userFacts.forEach((uf) => userFactMap[uf.userFactName] = uf);
	return userFactMap;
}

// Get UserFacts
export async function getUserFactList(userId: string,
	authCallback: (authTokenConfig: IPersonalizationAuthConfig) => Promise<string>): Promise<IUserFact[]> {
	let userFacts = getUnExpiredCachedUserFacts(userId);
	if (!userFacts || userFacts.length == 0) {
		try {
			const authToken = authCallback && await authCallback(authTokenConfigProd);
			userFacts = authToken && await fetchUserFactsWithTimeout(authToken);
		} catch (exception) {
			Logger.getLogger().event(EventIds.UserFactsCommonError, { error: getErrorMessage(exception) });
		}
	} else {
		Logger.getLogger().event(EventIds.UserFactsCommonCacheLoad,
			createTelemetryDataForCacheRead(userFacts?.length, getUserFactNames(userFacts)), getSamplingRate());
	}

	return userFacts;
}

// Get UserFacts
export async function getUserFacts(userId: string,
	authCallback: (authTokenConfig: IPersonalizationAuthConfig) => Promise<string>): Promise<IUserFactMap> {
	const userFactList = await getUserFactList(userId, authCallback);
	return userFactList && mapUserFacts(userFactList);
}

export function getUserFactManifest(): IUserFactRulesMap {
	return Configuration.get().getUserFactRules();
}

export function sendAppLastUsed(appId: number, lastUsedTime: Date): void {
	if (!AppLastUsedEventNames[appId]) {
		return;
	}
	processUserFactEvent(AppLastUsedEventNames[appId], lastUsedTime);
}

export function processTelemetryEvent(telemetryEvent: TelemetryEvent) {
	if (!telemetryEvent || !telemetryEvent.eventName) {
		return;
	}

	const userFactRules = getUserFactManifest();
	if (!userFactRules) {
		Logger.getLogger().event(EventIds.UserFactsNullTelemetryEventRulesMap, { Name: telemetryEvent.eventName }, getSamplingRate());
		return;
	}
	const manifest = userFactRules[telemetryEvent.eventName];
	
	try {
		if (!manifest) {
			// Manifest for this event does not exist
			return;
		}
		const cachedFactLastSourceTime = readLastSourceTimeFromCache(manifest.userFactName);
		const userFactTime = new Date(telemetryEvent.timestamp)

		if (!EvaluateTelemetryEvent(manifest, telemetryEvent, userFactTime, cachedFactLastSourceTime)) {
			// fact was not written to local storage
			return;
		}
		sendUserFact(manifest.userFactName, userFactTime)

	} catch (error) {
		Logger.getLogger().event(EventIds.UserFactsProcessTelemetryEventError, { error: getErrorMessage(error), Name: telemetryEvent.eventName });
		return;
	}
}

export function processUserFactEvent(eventName: string, userFactTime: Date) {
	if (!eventName || !userFactTime) {
		return;
	}

	const userFactRules = getUserFactManifest();
	if (!userFactRules) {
		Logger.getLogger().event(EventIds.UserFactsNullUserFactRulesMap, { Name: eventName }, getSamplingRate());
		return;
	}
	const manifest = userFactRules[eventName];

	try {
		if (!manifest) {
			// Manifest for this user fact event does not exist
			return;
		}
		const cachedFactLastSourceTime = readLastSourceTimeFromCache(manifest.userFactName);
		
		if (!EvaluateEvent(manifest, userFactTime, cachedFactLastSourceTime)) {
			// fact was not written to local storage
			return;
		} 
		sendUserFact(manifest.userFactName, userFactTime);
		return;
		
	} catch (error) {
		Logger.getLogger().event(EventIds.UserFactsProcessUserFactEventError, { error: getErrorMessage(error), Name: eventName });
		return;
	}
}

export function sendUserFact(userFactName: string, userFactTime: Date) {	
	Logger.getLogger().userFactEvent(userFactName, userFactTime.toISOString());
	writeFactToCache(userFactName, userFactTime.toISOString());
	Logger.getLogger().event(EventIds.UserFactsSendUserFactSuccess, { Name: userFactName }, getSamplingRate());
}

export function readLastSourceTimeFromCache(factKey: string): Date {
	let cachedUserFacts;
	try {
		cachedUserFacts = mapUserFacts(getCachedUserFacts());
		if (!cachedUserFacts) {
			Logger.getLogger().event(EventIds.UserFactsEmptyCache, { Name: factKey });
			return null;
		}

		const lastSourceTime = (cachedUserFacts[factKey]) ? cachedUserFacts[factKey].sourceDateTime : null;
		if (!lastSourceTime) {
			return null;
		}
		return new Date(lastSourceTime);

	} catch(error) {
		Logger.getLogger().event(EventIds.UserFactsReadCacheError, { error: getErrorMessage(error), Name: factKey });
		return null;
	}
}

export function writeFactToCache(factKey: string, factTime: string) {
	let cachedUserFacts;
	try {
		cachedUserFacts = getCachedUserFacts();

		// Do not set expiration if there are no cached entries in first time boot scenarios.
		// This to let subsequent service call go through.
		const cacheExpiration = cachedUserFacts ? getSetting<number>("userFactsCacheExpiration") : -1;
		const mergedFacts = addOrUpdateAppUsageUserFact(factKey, factTime, cachedUserFacts);
		
		updateCache(ContentType.userFacts, mergedFacts, cacheExpiration, UserFactLocalStorageKey);
	} catch(error) {
		Logger.getLogger().event(EventIds.UserFactsWriteCacheError, { error: getErrorMessage(error), Name: factKey })
	}
}

function createTelemetryDataForCacheRead(count = -1, factNames: string[] = undefined): Logger.ICustomLogData {
	return {
		Count: count,
		FactNames: createStringOfFactNames(factNames),
	};
}
